import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useForm, useFormState } from "react-final-form";
import classNames from "classnames";
import { get } from "lodash";
import Input from "rs-components/Input";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import NumberInput from "rs-components/Input/components/NumberInput";
import Button, { ButtonStyle } from "components/Button";
import IconButton from "components/IconButton";
import RadioInputGroup from "components/Controls/RadioInputGroup";
import SearchSelectInput from "rs-components/Input/components/SearchSelectInput";
import RangeValue from "rs-components/RangeValue";
import HtmlText from "components/HtmlText";
import { eventValueTypes, findDictionaryItemByValue, recipeParameterUnitsType, searchDirections, timeUnits, } from "shared/rsDictionary";
import { isNotEmpty } from "utils/validate";
import { FinalFormMutators } from "utils/final-form-mutators";
import { getSelectedVariable, getUnitsCategory } from "store/recipe/recipe.utils";
import { getFieldRestrictions, getRestrictionDefaultValue, } from "store/knowledge-base-item/knowledge-base-item.utils";
import { processVariablesSelector, parameterTypesSelector } from "store/global/global.selectors";
import { ComplexValueParameterType, CustomUnitType, EventValueType } from "store/recipe/recipe.types";
import { TimestampType, } from "shared/types";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { getIsTimestampFilled } from "store/rs-workspace/utils/insights.utils";
import { parseComplexUnitToHtml } from "store/recipe/custom-variables.utils";
import { getProcessVariablesToSelect } from "store/rs-workspace/utils/results.utils";
import { recipeCustomVariablesSelector } from "store/rs-workspace/selectors/rs-workspace.selectors";
import ClearIcon from "icons/close.svg";
import * as styles from "./styles.module.scss";
export var TimingFormFields;
(function (TimingFormFields) {
    TimingFormFields["timestampType"] = "timestampType";
    TimingFormFields["timestampId"] = "timestampId";
    TimingFormFields["offset"] = "offset";
    TimingFormFields["offsetUnit"] = "offsetUnit";
    TimingFormFields["cutEvent"] = "cutEvent";
})(TimingFormFields || (TimingFormFields = {}));
var CutEventFormFields;
(function (CutEventFormFields) {
    CutEventFormFields["searchDirection"] = "cutEvent.searchDirection";
    CutEventFormFields["variable"] = "cutEvent.variable";
    CutEventFormFields["customVariable"] = "cutEvent.customVariable";
    CutEventFormFields["operation"] = "cutEvent.operation";
})(CutEventFormFields || (CutEventFormFields = {}));
var CutEventOperationFormFields;
(function (CutEventOperationFormFields) {
    CutEventOperationFormFields["value"] = "value";
    CutEventOperationFormFields["min"] = "min";
    CutEventOperationFormFields["max"] = "max";
    CutEventOperationFormFields["units"] = "units";
    CutEventOperationFormFields["type"] = "type";
})(CutEventOperationFormFields || (CutEventOperationFormFields = {}));
const TimingFields = ({ label, name, values, additionalTimestampOptions = [], excludedVariables, restrictions, namePrefix, recipeIds = [], }) => {
    const form = useForm();
    const formValues = useFormState().values;
    const processVariables = useSelector(processVariablesSelector);
    const customVariables = useSelector(recipeCustomVariablesSelector(recipeIds));
    const parameterTypes = useSelector(parameterTypesSelector);
    const editable = useSelector(isEditableSelector);
    const selectedVariable = useMemo(() => getSelectedVariable(customVariables, processVariables, values?.cutEvent?.customVariable, values?.cutEvent?.variable), [customVariables, processVariables, values]);
    const isRangeEventValue = useMemo(() => values?.cutEvent?.operation?.type === EventValueType.OUT_OF_RANGE, [values]);
    const allowedParameterUnits = useMemo(() => getUnitsCategory(parameterTypes, selectedVariable?.units)?.variablesList?.map(({ type }) => type) ?? [], [parameterTypes, selectedVariable?.units]);
    const dictionaryAllowedParameterUnits = useMemo(() => allowedParameterUnits.map((value) => findDictionaryItemByValue(recipeParameterUnitsType, value) ?? { value, label: value }), [allowedParameterUnits]);
    // added namePrefix to allow passing non-standard restrictions path
    const restrictionsByName = useMemo(() => getFieldRestrictions(restrictions, [namePrefix ? name.replace(namePrefix, "") : name]), [name, namePrefix, restrictions]);
    const cutEventRestrictions = useMemo(() => getFieldRestrictions(restrictionsByName, [TimingFormFields.cutEvent]), [restrictionsByName]);
    const eventValueRestrictions = useMemo(() => getFieldRestrictions(cutEventRestrictions, ["Operation", values?.cutEvent?.operation?.type]), [cutEventRestrictions, values?.cutEvent?.operation?.type]);
    const timestampFilled = useMemo(() => getIsTimestampFilled(formValues?.[name]), [formValues, name]);
    const timestampOptions = useMemo(() => [
        { value: { type: TimestampType.Start }, label: "Start" },
        ...additionalTimestampOptions,
        { value: { type: TimestampType.End }, label: "End" },
    ], [additionalTimestampOptions]);
    const getSelectedTimestamp = useCallback((type, id) => timestampOptions.find(({ value }) => value.type === type && value.id === id), [timestampOptions]);
    const timestampRestrictions = useMemo(() => {
        const typeRestrictions = getFieldRestrictions(restrictionsByName, [TimingFormFields.timestampType]);
        const idDefault = getRestrictionDefaultValue(restrictionsByName, [TimingFormFields.timestampId]);
        const typeDefault = getRestrictionDefaultValue(typeRestrictions, []);
        return {
            ...typeRestrictions,
            ...(isNotEmpty(typeDefault) || isNotEmpty(idDefault)
                ? {
                    Default: {
                        ...(isNotEmpty(typeDefault) ? { type: typeDefault } : {}),
                        ...(isNotEmpty(idDefault) ? { id: idDefault } : {}),
                    },
                }
                : {}),
        };
    }, [restrictionsByName]);
    const cutEventValueNames = useMemo(() => {
        const valueName = `${name}.${CutEventFormFields.operation}.${CutEventOperationFormFields.value}`;
        const minName = `${name}.${CutEventFormFields.operation}.${CutEventOperationFormFields.min}`;
        const maxName = `${name}.${CutEventFormFields.operation}.${CutEventOperationFormFields.max}`;
        return {
            valueName,
            value: {
                valueName: `${valueName}.${CutEventOperationFormFields.value}`,
                unitsName: `${valueName}.${CutEventOperationFormFields.units}`,
            },
            minName,
            min: {
                valueName: `${minName}.${CutEventOperationFormFields.value}`,
                unitsName: `${minName}.${CutEventOperationFormFields.units}`,
            },
            maxName,
            max: {
                valueName: `${maxName}.${CutEventOperationFormFields.value}`,
                unitsName: `${maxName}.${CutEventOperationFormFields.units}`,
            },
        };
    }, [name]);
    const resetCutEventValue = useCallback((operationType) => {
        const isRangeEventValue = (operationType ?? values?.cutEvent?.operation?.type) === EventValueType.OUT_OF_RANGE;
        if (isRangeEventValue) {
            form.change(cutEventValueNames.minName, undefined);
            form.change(cutEventValueNames.maxName, undefined);
            const mutator = form.mutators[FinalFormMutators.resetNestedFieldsTouchedMutator];
            if (mutator) {
                mutator(cutEventValueNames.minName);
                mutator(cutEventValueNames.maxName);
            }
        }
        else {
            form.change(cutEventValueNames.valueName, undefined);
        }
    }, [
        cutEventValueNames.maxName,
        cutEventValueNames.minName,
        cutEventValueNames.valueName,
        form,
        values?.cutEvent?.operation?.type,
    ]);
    const setDefaultParameterType = useCallback(() => {
        const isRangeEventValue = values?.cutEvent?.operation?.type === EventValueType.OUT_OF_RANGE;
        if (isRangeEventValue) {
            if (!values?.cutEvent?.operation?.min) {
                form.change(`${cutEventValueNames.minName}.${CutEventOperationFormFields.type}`, ComplexValueParameterType.ANY_UNIT_VALUE);
            }
            if (!values?.cutEvent?.operation?.max) {
                form.change(`${cutEventValueNames.maxName}.${CutEventOperationFormFields.type}`, ComplexValueParameterType.ANY_UNIT_VALUE);
            }
        }
        else {
            if (!values?.cutEvent?.operation?.value?.type) {
                form.change(`${cutEventValueNames.valueName}.${CutEventOperationFormFields.type}`, ComplexValueParameterType.ANY_UNIT_VALUE);
            }
        }
    }, [
        cutEventValueNames.maxName,
        cutEventValueNames.minName,
        cutEventValueNames.valueName,
        form,
        values?.cutEvent?.operation,
    ]);
    const renderCutEventUnitsInput = useMemo(() => {
        if (values?.cutEvent?.customVariable) {
            const baseCustomVariable = customVariables?.find(({ id }) => id === values?.cutEvent?.customVariable);
            return (React.createElement(HtmlText, { targetClassName: styles.unit, text: parseComplexUnitToHtml(baseCustomVariable?.formula?.configuredUnits?.complexValue)?.html }));
        }
        return (React.createElement(Input, { component: SelectInput, itemRenderer: SelectItemRenderer, readonly: !editable, placeholder: !values?.cutEvent?.variable ? "Select a variable first." : "Select", label: "Unit", name: isRangeEventValue ? cutEventValueNames.min.unitsName : cutEventValueNames.value.unitsName, items: dictionaryAllowedParameterUnits, disableFormChange: true, onChange: ({ value }) => form.batch(() => {
                if (isRangeEventValue) {
                    form.change(cutEventValueNames.min.unitsName, value);
                    form.change(cutEventValueNames.max.unitsName, value);
                }
                else {
                    form.change(cutEventValueNames.value.unitsName, value);
                }
            }), restrictions: (eventValueRestrictions?.Value ?? eventValueRestrictions?.Min)?.AnyUnitValue?.Units ?? {
                IsNecessary: true,
            }, classes: {
                label: styles["input-label"],
                component: styles.select,
                container: isRangeEventValue ? styles["event-value-minMaxUnits"] : styles["event-value-units"],
            } }));
    }, [
        cutEventValueNames.max.unitsName,
        cutEventValueNames.min.unitsName,
        cutEventValueNames.value.unitsName,
        dictionaryAllowedParameterUnits,
        editable,
        eventValueRestrictions?.Min,
        eventValueRestrictions?.Value,
        form,
        isRangeEventValue,
        values?.cutEvent?.variable,
        values?.cutEvent?.customVariable,
        customVariables,
    ]);
    return (React.createElement("div", { className: styles.root },
        label && React.createElement("p", { className: styles["root-label"] }, label),
        React.createElement("div", { className: styles.timestamp },
            React.createElement(Input, { component: SelectInput, itemRenderer: SelectItemRenderer, readonly: !editable, label: "Timestamp", autoFocus: true, placeholder: "Select", name: `${name}.${TimingFormFields.timestampType}`, value: getSelectedTimestamp(values?.timestampType, values?.timestampId ?? undefined), disableFormChange: true, onChange: ({ value: { type, id } }) => {
                    form.batch(() => {
                        form.change(`${name}.${TimingFormFields.timestampType}`, type);
                        form.change(`${name}.${TimingFormFields.timestampId}`, id);
                    });
                }, suppressStateClass: timestampFilled, items: timestampOptions, restrictions: timestampRestrictions, classes: {
                    label: styles["input-label"],
                    component: styles.select,
                    container: styles["timestamp-id"],
                } }),
            React.createElement(Input, { component: NumberInput, readonly: !editable, label: "Offset", placeholder: "Value", name: `${name}.${TimingFormFields.offset}`, restrictions: {
                    IsNumber: true,
                    ...getFieldRestrictions(restrictionsByName, [TimingFormFields.offset]),
                }, classes: { label: styles["input-label"], container: styles["timestamp-offset"] } }),
            React.createElement("div", { className: styles["timestamp-units"] },
                React.createElement("p", { className: classNames(styles["input-label"], styles.extraMargin) }, "\u200B"),
                React.createElement(Input, { component: SelectInput, itemRenderer: SelectItemRenderer, readonly: !editable, placeholder: "Select", name: `${name}.${TimingFormFields.offsetUnit}`, items: timeUnits, valueFormatter: ({ value }) => value, restrictions: getFieldRestrictions(restrictionsByName, [TimingFormFields.offsetUnit]), classes: { component: styles.select } }))),
        !values?.cutEvent && editable ? (React.createElement(Button, { label: "Add event", style: ButtonStyle.Tertiary, classes: { root: styles.addEventButton }, onClick: () => form.change(`${name}.${TimingFormFields.cutEvent}`, {}) })) : (React.createElement("div", { className: styles["event"] },
            React.createElement("p", { className: styles["root-label"] },
                "Event",
                editable && (React.createElement(IconButton, { icon: ClearIcon, title: "Remove", classes: { root: styles["event-clearIcon"] }, onClick: () => form.change(`${name}.${TimingFormFields.cutEvent}`) }))),
            React.createElement(Input, { component: RadioInputGroup, name: `${name}.${CutEventFormFields.searchDirection}`, readonly: !editable, label: "Search", items: searchDirections, restrictions: getFieldRestrictions(cutEventRestrictions, ["SearchDirection"]), classes: { container: styles["event-typeInput"], label: styles["input-label"] } }),
            React.createElement(Input, { component: SearchSelectInput, itemRenderer: SelectItemRenderer, readonly: !editable, label: "Variable", placeholder: "Select", name: `${name}.${typeof selectedVariable?.key === "number"
                    ? CutEventFormFields.customVariable
                    : CutEventFormFields.variable}`, value: selectedVariable ? { value: selectedVariable, label: selectedVariable.name } : undefined, items: getProcessVariablesToSelect(processVariables, customVariables, ({ recordInData }) => Boolean(recordInData)).filter(({ value }) => !excludedVariables?.some((variable) => variable?.value === value?.key)), valueFormatter: ({ key }) => key, restrictions: getFieldRestrictions(cutEventRestrictions, ["Variable"]), onChange: (value) => {
                    form.batch(() => {
                        const customVariableSelected = typeof value === "number";
                        if (customVariableSelected) {
                            form.change(`${name}.${CutEventFormFields.variable}`, null);
                            form.change(`${name}.${CutEventFormFields.customVariable}`, value);
                        }
                        else {
                            form.change(`${name}.${CutEventFormFields.variable}`, value);
                            form.change(`${name}.${CutEventFormFields.customVariable}`, null);
                        }
                        resetCutEventValue();
                        if (customVariableSelected) {
                            form.change(cutEventValueNames.value.unitsName, CustomUnitType.CustomUnits);
                            form.change(`${cutEventValueNames.valueName}.${CutEventOperationFormFields.type}`, ComplexValueParameterType.ANY_UNIT_VALUE);
                        }
                    });
                }, classes: {
                    label: styles["input-label"],
                    component: styles.select,
                    container: styles["event-variable"],
                } }),
            React.createElement("div", { className: styles["event-value"] },
                React.createElement(Input, { component: SelectInput, itemRenderer: SelectItemRenderer, readonly: !editable, label: "Operation", placeholder: "Select", name: `${name}.${CutEventFormFields.operation}.${CutEventOperationFormFields.type}`, items: eventValueTypes, disableFormChange: true, onChange: ({ value }, prevValue) => {
                        if (value !== prevValue) {
                            form.batch(() => {
                                form.change(`${name}.${CutEventFormFields.operation}.${CutEventOperationFormFields.type}`, value);
                                resetCutEventValue(value);
                                if (isNotEmpty(get(values, CutEventFormFields.customVariable))) {
                                    form.change(cutEventValueNames.value.unitsName, CustomUnitType.CustomUnits);
                                    form.change(`${cutEventValueNames.valueName}.${CutEventOperationFormFields.type}`, ComplexValueParameterType.ANY_UNIT_VALUE);
                                }
                            });
                        }
                    }, restrictions: getFieldRestrictions(cutEventRestrictions, ["Operation"]), classes: {
                        label: styles["input-label"],
                        component: styles.select,
                        container: styles["event-value-operation"],
                    } }),
                !isRangeEventValue && (React.createElement(React.Fragment, null,
                    React.createElement(Input, { component: NumberInput, readonly: !editable, disabled: !values?.cutEvent?.variable && !values?.cutEvent?.customVariable, label: "Value", placeholder: "Value", name: cutEventValueNames.value.valueName, restrictions: {
                            IsNumber: true,
                            ...(eventValueRestrictions?.Value?.AnyUnitValue?.Value ?? { IsNecessary: true }),
                        }, classes: { label: styles["input-label"], container: styles["event-value-value"] }, onChange: setDefaultParameterType }),
                    renderCutEventUnitsInput))),
            isRangeEventValue && (React.createElement("div", { className: styles["event-value"] },
                React.createElement(RangeValue, { from: {
                        label: "Min",
                        component: NumberInput,
                        name: cutEventValueNames.min.valueName,
                        classes: {
                            label: styles["input-label"],
                        },
                        onChange: setDefaultParameterType,
                        placeholder: "Value",
                        disabled: !values?.cutEvent?.variable && !values?.cutEvent?.customVariable,
                        restrictions: {
                            IsNumber: true,
                            ...(eventValueRestrictions?.Min?.AnyUnitValue?.Value ?? { IsNecessary: true }),
                        },
                    }, to: {
                        label: "Max",
                        name: cutEventValueNames.max.valueName,
                        component: NumberInput,
                        classes: {
                            label: styles["input-label"],
                        },
                        onChange: setDefaultParameterType,
                        placeholder: "Value",
                        disabled: !values?.cutEvent?.variable && !values?.cutEvent?.customVariable,
                        restrictions: {
                            IsNumber: true,
                            ...(eventValueRestrictions?.Max?.AnyUnitValue?.Value ?? { IsNecessary: true }),
                        },
                    }, className: styles["event-value-minMax"], editable: editable }),
                renderCutEventUnitsInput))))));
};
export default TimingFields;
